import React, { useState, useCallback, useMemo } from 'react'
import { Box } from '@mui/material'
import BoardList from '@components/Board/BoardList'
import BoardListCard from '@components/Board/BoardListCard'
import BasicModal from '@components/Modal/BasicModal'
import AlertConfirmModal from '@/components/Modal/AlertConfirmModal'
import { deleteMyContentsConcern, fetchMyContentsHistoryList, insertMyContentsConcern } from '@/api/mypage'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@/data/nav'
import { useConfirm } from '@/components/ConfirmProvider'
import { useAuth } from '@/components/AuthContext'

export default function MyViewed() {
    const lang = useLang()
    const [openModal, setOpenModal] = useState(false)
    const [selected, setSelected] = useState(null)

    const [confirmedSearch, setConfirmedSearch] = useState('')
    const [data, setData] = useState(null);

    const [confirmOpen, setConfirmOpen] = useState(false)
    const [itemToToggle, setItemToToggle] = useState(null);
    const [modalDisplayItem, setModalDisplayItem] = useState(null);

    const { alert } = useConfirm();
    const { user, loading, isAuthReady } = useAuth();

    const fetcher = useCallback(
        async ({ page = 1, pageSize = 6, keyword = '' }) => {
            if (data && data.page === page && data.pageSize === pageSize && data.keyword === confirmedSearch) {
                return { items: data.items, total: data.total };
            }

            const params = {
                page,
                pageSize,
                keyword: confirmedSearch,
                accountId: user.accountId
            }
            try {
                const res = await fetchMyContentsHistoryList({ params });
                const items = res.data.content.map(item => ({
                    id: item.cmsContentsId,
                    title: item.cmsContentsName,
                    xr: item?.contentType,
                    preview: !!item.previewUrl,
                    interest: item?.concernYn === 'Y',
                    institution: false,
                    category: item.lcmsTaxonomyFullpath,
                    previewUrl: item.previewUrl,
                    thumbUrl: item?.thumbnailImgUrl, //|| imgArr[Math.floor(Math.random() * imgArr.length)].thumbUrl,
                    href: buildLangHref(`/contents/xrview/${item.cmsContentsId}`, lang),
                }));
                const total = res.data.totalElements;
                setData({ items, total, page, pageSize, keyword: confirmedSearch });
                return { items, total };
            } catch (error) {
                console.error('Error fetching my contents concern list:', error);
                setData(null);
                return { items: [], total: 0 };
            }
        },
        [confirmedSearch, data]
    )

    const handlePreviewClick = useCallback((row) => {
        setSelected(row)
        setOpenModal(true)
    }, [])

    const toggleInterest = useCallback((row) => {
        setItemToToggle(row);
        setModalDisplayItem(row);
        setConfirmOpen(true);
    }, [])

    const handleConfirmToggle = useCallback(async () => {
        if (!itemToToggle) return;

        try {
            const params = {
                cmsContentsId: itemToToggle.id,
                accountId: user.accountId
            };
            if(itemToToggle.interest) {
                await deleteMyContentsConcern(params);
            } else {
                await insertMyContentsConcern(params);
            }
            alert(itemToToggle.interest ? "관심콘텐츠 목록에서 삭제되었습니다." : "관심콘텐츠 목록에 등록되었습니다.");
            setData(null); // Invalidate cache to trigger re-fetch
        } catch (error) {
            console.error('Failed to delete interested content', error);
            // Here we could show an error alert to the user.
        } finally {

            setConfirmOpen(false);
            setItemToToggle(null);
        }
    }, [itemToToggle]);
    return (
        <>
            <BoardList
                view="card"
                columns={[]}
                fetcher={fetcher}
                initialPage={1}
                initialPageSize={6}
                initialSort={{ field: 'id', direction: 'desc' }}
                CardComponent={BoardListCard}
                CardProps={{
                    gridCols: 3,
                    onPreviewClick: handlePreviewClick,
                    onInterestClick: toggleInterest,
                }}
                onSearchSubmit={(searchValue) => {
                    setConfirmedSearch(searchValue)
                }}
                showDefaultToolbar={true}
                emptyMessage='내가본콘텐츠가 없습니다.'
            />
            <AlertConfirmModal
                open={confirmOpen}
                title={modalDisplayItem?.interest ? '관심컨텐츠 삭제' : '관심컨텐츠 등록'}
                description={modalDisplayItem?.interest ? `${modalDisplayItem?.title}\n해당 콘텐츠를 삭제하시겠습니까?` : `${modalDisplayItem?.title}\n해당 콘텐츠를 등록하시겠습니까?`}
                type="confirm"
                onConfirm={handleConfirmToggle}
                onClose={() => setConfirmOpen(false)}
                onCancel={() => {
                    setConfirmOpen(false)
                    setItemToToggle(null)
                }}
            />
            <BasicModal
                open={openModal}
                onClose={() => setOpenModal(false)}
                title={selected?.title}
                disableEnforceFocus
                disablePortal
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '100%',
                        paddingTop: '56.25%',
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    <Box
                        component="iframe"
                        src={selected?.previewUrl || null}
                        title="YouTube video player"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 0,
                        }}
                    />
                </Box>
            </BasicModal>
        </>
    )
}
